/* eslint-disable react/jsx-no-undef */
import { Box, Button, Menu, MenuItem, CircularProgress } from '@mui/material';
import styles from './Offices.module.css';
import BasePageStructure from 'src/components/BasePageStructure';
import { useEffect, useState } from 'react';
import { EmployeeState, EmployeesService, GetEmployees_Out, GetOffice_Out, OfficesService, TransferType } from 'src/pehra-ts-client';
import RefreshIcon from '@mui/icons-material/Refresh';
import NewOfficeModal from './components/NewOfficeModal';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ConditionalRenderQuery from 'src/components/ConditionalRenderQuery';
import OfficesList from './components/OfficesList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EmployeeList from './components/EmployeeList';
import OfficeTreeView from './components/OfficeTreeView';
import MoveOfficeModal from './components/MoveOfficeModal';
import TransferPoolList from './components/TransferPoolList';
import DeleteConfirmationModal from 'src/components/DeleteConfirmationModal';
import useNotification from 'src/hooks/useNotification';
import EditOfficeDetailsModal from './components/EditOfficeDetailsModal';

export interface IOffices {}

const Offices: React.FC<IOffices> = () => {
  const [parentOfficeDetail, setParentOfficeDetail] = useState<GetOffice_Out | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openNewOfficeModal, setOpenNewOfficeModal] = useState<boolean>(false);
  const [openMoveOfficeModal, setOpenMoveOfficeModal] = useState<boolean>(false);
  const [openDeleteOfficeModal, setOpenDeleteOfficeModal] = useState<boolean>(false);
  const [openEditOfficeModal, setOpenEditOfficeModal] = useState<boolean>(false);

  const [sendNotification] = useNotification();

  const open = Boolean(anchorEl);

  const queryClient = useQueryClient();

  let { parentId = null } = useParams();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClosNewOfficeModal = () => {
    setOpenNewOfficeModal(false);
  };

  const fetchOfficesQuery = useQuery({
    queryKey: ['officesList', parentId],
    queryFn: () => OfficesService.getAllOffices(parentId)
  });

  const fetchParentOfficeQuery = useQuery({
    queryKey: ['office', parentId],
    queryFn: () => OfficesService.getOffice(parentId as string),
    enabled: parentId !== null
  });

  // TODO : replace with office query later
  const fetchEmployeeAtOfficeQuery = useQuery({
    queryKey: ['employeesList', parentId],
    queryFn: () =>
      EmployeesService.getAllEmployees({
        current_office_id: parentId,
        current_office_type: TransferType.PERMANENT
      }),
    enabled: parentId !== null
  });

  // TODO : replace with office query later
  const fetchTransferPoolEmployeeAtOfficeQuery = useQuery({
    queryKey: ['employeesList', parentId, 'AVAILABLE_FOR_TRANSFER'],
    queryFn: () =>
      EmployeesService.getAllEmployees({
        current_office_id: parentId,
        state: EmployeeState.AVAILABLE_FOR_TRANSFER,
        current_office_type: TransferType.TEMPORARY
      }),
    enabled: parentId !== null
  });

  const deleteOffice = async () => {
    try {
      const res = await OfficesService.deleteOffice(parentId as string);
    } catch (error) {
      sendNotification({
        msg: 'Office deletion failed',
        type: 'error'
      });
      throw error;
    } finally {
      sendNotification({
        msg: 'Office deleted successfully',
        type: 'success'
      });
      setOpenDeleteOfficeModal(false);
      queryClient.invalidateQueries({
        queryKey: ['officesList', parentId]
      });
    }

    // Close the modal
    fetchOfficesQuery.refetch();
    setOpenDeleteOfficeModal(false);
  };

  const getTitle = () => {
    if (fetchParentOfficeQuery.data) {
      return `Office ( ${fetchParentOfficeQuery.data.office_name} )`;
    }
    return 'Office';
  };

  const OfficeComponent = (
    <ConditionalRenderQuery
      status={fetchOfficesQuery.status}
      success={() => {
        if (fetchOfficesQuery.data?.offices === undefined) {
          return null;
        } else {
          return (
            <OfficesList
              parentOfficeDetail={fetchParentOfficeQuery.data || null}
              parentId={parentId}
              offices={fetchOfficesQuery.data?.offices}
              handleNewOfficeClick={() => {
                setOpenNewOfficeModal(true);
              }}
            />
          );
        }
      }}
    />
  );

  const EmployeeComponent = (
    <ConditionalRenderQuery
      status={fetchEmployeeAtOfficeQuery.status}
      success={() => {
        if (fetchEmployeeAtOfficeQuery.data === undefined) {
          return null;
        }
        return <EmployeeList officeId={parentId as string} data={fetchEmployeeAtOfficeQuery.data?.employees} />;
      }}
    />
  );

  const TransferPoolComponent = (
    <ConditionalRenderQuery
      status={fetchTransferPoolEmployeeAtOfficeQuery.status}
      success={() => {
        if (fetchTransferPoolEmployeeAtOfficeQuery.data === undefined) {
          return null;
        }
        return <TransferPoolList officeId={parentId as string} data={fetchTransferPoolEmployeeAtOfficeQuery.data?.employees} />;
      }}
    />
  );

  const OfficePropertiesMenu = parentId ? (
    <Box>
      <Button
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        variant="outlined"
        endIcon={<KeyboardArrowDownIcon />}
      >
        Properties
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
      >
        <MenuItem
          onClick={() => {
            setOpenEditOfficeModal(true);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenMoveOfficeModal(true);
          }}
        >
          Move
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenDeleteOfficeModal(true);
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Box>
  ) : undefined;

  const handleOfficeRefresh = () => {
    fetchOfficesQuery.refetch();
    fetchParentOfficeQuery.refetch();
    fetchEmployeeAtOfficeQuery.refetch();
    fetchTransferPoolEmployeeAtOfficeQuery.refetch();
  };
  const headerMenu = (
    <Box className={styles.actionContainer}>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={handleOfficeRefresh}
        disabled={fetchOfficesQuery.isFetching} // Disable button while fetching
        startIcon={fetchOfficesQuery.isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
      >
        {fetchOfficesQuery.isFetching ? 'Refreshing...' : 'Refresh'}
      </Button>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => {
          setOpenNewOfficeModal(true);
        }}
      >
        Add New Office
      </Button>
    </Box>
  );

  useEffect(() => {
    if (fetchParentOfficeQuery.isSuccess) {
      setParentOfficeDetail(fetchParentOfficeQuery.data);
    }
  }, [fetchParentOfficeQuery.data, fetchParentOfficeQuery.isSuccess]);

  const officeTreeView = (
    <Box className={styles.officeTreeView}>
      <OfficeTreeView currentId={parentId} />
    </Box>
  );

  return (
    <BasePageStructure title={getTitle()} additionalHeaderMenu={OfficePropertiesMenu} showBackButton={parentId !== null}>
      <Box className={styles.searchContainer}>{headerMenu}</Box>
      <Box className={styles.container}>
        {/* Display offices  */}
        {fetchOfficesQuery.isFetching ? (
          <CircularProgress />
        ) : (
          <Box className={styles.officesContainer}>
            <Box className={styles.officeTreeViewContainer}>{officeTreeView}</Box>
            <Box className={styles.officesListContainer}>{OfficeComponent}</Box>
          </Box>
        )}
        {/* Display Employees in particular office */}
        {parentId ? EmployeeComponent : null}
        {parentId ? TransferPoolComponent : null}
      </Box>
      <NewOfficeModal
        openModal={openNewOfficeModal}
        handleCloseModal={handleClosNewOfficeModal}
        parentOfficeDetail={fetchParentOfficeQuery.data || null}
      />
      <MoveOfficeModal
        openModal={openMoveOfficeModal}
        handleCloseModal={() => {
          setOpenMoveOfficeModal(false);
        }}
        currentOfficeDetail={fetchParentOfficeQuery.data || null}
      />
      <DeleteConfirmationModal
        openDeleteModal={openDeleteOfficeModal}
        handleCloseDeleteModal={() => {
          setOpenDeleteOfficeModal(false);
        }}
        handleDelete={deleteOffice}
      />
      <EditOfficeDetailsModal
        openModal={openEditOfficeModal}
        handleCloseModal={() => {
          setOpenEditOfficeModal(false);
        }}
        officeDetail={fetchParentOfficeQuery.data}
      />
    </BasePageStructure>
  );
};

export default Offices;
